import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";

import useBrochurePortraitUpload from "../../../hooks/useBrochurePortraitUpload";
import { SlotCarousel } from "./BrochureImageSlots";
import { BrochureTemplateCards, TemplateSketch } from "./BrochureTemplateCards";
import {
  appendPrecisToBody,
  artistBiography,
  defaultPortraitCaption,
  detectImageOrientation,
  FREE_TEXT_IMAGE_POSITIONS,
  IMAGE_ORIENTATIONS,
  insertSectionPage,
  isFreeTextTemplate,
  nextPageKey,
  patchForPickedImage,
  PORTRAIT_FILE_ACCEPT,
  portraitFileError,
  removeSectionPage,
  reorderList,
  sectionPage,
  sectionPageId,
  templateDescription,
  templateLabel,
  templateSlots,
  updateSectionPage,
  usesImageOrientation,
} from "./wizardUtils";

/**
 * The three shapes, drawn as the frame each one is — a tall rectangle, a wide
 * one, a square — all on the same field so they read as one set.
 */
const ORIENTATION_ICON_SIZES = {
  portrait: { width: 9, height: 13 },
  landscape: { width: 14, height: 9.5 },
  square: { width: 11.5, height: 11.5 },
};

const ORIENTATION_ICON_FIELD = 16;

const OrientationIcon = ({ orientation, className = "h-4 w-4" }) => {
  const { width, height } = ORIENTATION_ICON_SIZES[orientation];
  return (
    <svg
      viewBox={`0 0 ${ORIENTATION_ICON_FIELD} ${ORIENTATION_ICON_FIELD}`}
      className={`${className} shrink-0`}
      role="presentation"
    >
      <rect
        x={(ORIENTATION_ICON_FIELD - width) / 2}
        y={(ORIENTATION_ICON_FIELD - height) / 2}
        width={width}
        height={height}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      />
    </svg>
  );
};

/**
 * Which shape the Artwork Hero treats its image as, and so which edge the image
 * is anchored by. Picking an image sets this from the image itself; these three
 * override it, for a photograph whose proportions aren't how the page should
 * read.
 */
const ImageOrientationPicker = ({ value, onChange, disabled }) => (
  <div className="min-w-0">
    <div className="text-xs font-medium text-gray-700">Image shape</div>
    <div className="mt-1 flex flex-wrap gap-2">
      {IMAGE_ORIENTATIONS.map(option => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          aria-pressed={value === option.id}
          onClick={() => onChange(option.id)}
          className={`flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition disabled:opacity-50 ${
            value === option.id
              ? "border-blue-600 bg-blue-50 font-medium text-blue-700"
              : "border-gray-200 text-gray-600 hover:border-gray-400"
          }`}
        >
          <OrientationIcon orientation={option.id} />
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

/**
 * The portrait printed beside an artist's biography, in the 4:5 frame the page
 * gives it. One portrait per artist for the whole brochure, uploaded straight to
 * S3 and belonging to this report alone — the artist's own record is untouched.
 *
 * An artist without one prints the placeholder frame, so a page is never
 * blocked on finding a photograph.
 */
const ArtistPortraitPicker = ({ artistName, handle, onChange, disabled }) => {
  const { uploadPortrait, fetchPortraitViewUrl, uploading } =
    useBrochurePortraitUpload();
  // Which handle the shown image belongs to, so a portrait saved in an earlier
  // session is fetched once and a freshly uploaded one isn't fetched at all.
  const [preview, setPreview] = useState({ handle: null, url: null });
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!handle || preview.handle === handle) return undefined;
    let current = true;
    fetchPortraitViewUrl(handle).then(url => {
      if (current) setPreview({ handle, url });
    });
    return () => {
      current = false;
    };
  }, [handle, preview.handle, fetchPortraitViewUrl]);

  const handlePick = async event => {
    const [file] = event.target.files || [];
    // Cleared so choosing the same file twice still counts as a change.
    event.target.value = "";
    if (!file) return;

    const problem = portraitFileError(file);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);

    const result = await uploadPortrait(file);
    if (!result.success) {
      setError(String(result.error));
      return;
    }
    setPreview({ handle: result.handle, url: result.viewUrl });
    onChange(result.handle);
  };

  const shownUrl = preview.handle === handle ? preview.url : null;

  return (
    <div className="flex min-w-0 gap-3">
      <div className="h-24 w-[76.8px] shrink-0 overflow-hidden border border-gray-200 bg-gray-50">
        {shownUrl ? (
          <img
            src={shownUrl}
            alt={`Portrait of ${artistName}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            {handle ? "Loading…" : "Portrait"}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-col items-start gap-1">
        <input
          ref={fileInputRef}
          type="file"
          accept={PORTRAIT_FILE_ACCEPT}
          className="hidden"
          onChange={handlePick}
        />
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => fileInputRef.current?.click()}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 transition hover:border-gray-500 disabled:opacity-50"
        >
          {uploading
            ? "Uploading…"
            : handle
              ? "Replace portrait"
              : "Upload a portrait"}
        </button>
        {handle ? (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => {
              setError(null);
              onChange(null);
            }}
            className="text-xs text-gray-500 transition hover:text-red-600 disabled:opacity-50"
          >
            Remove
          </button>
        ) : null}
        <div className="text-xs text-gray-500">PNG or JPG, up to 1MB.</div>
        {error ? <div className="text-xs text-red-600">{error}</div> : null}
      </div>
    </div>
  );
};

/**
 * One artist's biography page: the words, which start as whatever their contact
 * record says, the portrait that prints beside them, and the caption under it,
 * which starts as the default sentence naming them. Editing any of the three
 * changes this brochure and nothing else.
 */
const BiographyFields = ({
  artist,
  biography,
  onBiographyChange,
  portraitHandle,
  onPortraitChange,
  portraitCaption,
  onPortraitCaptionChange,
  disabled,
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <div className="text-xs font-medium text-gray-700">{artist.name}</div>
    <textarea
      className="h-40 rounded border border-gray-300 px-2 py-1.5 text-sm"
      value={biography}
      disabled={disabled}
      placeholder="A paragraph or two about the artist…"
      onChange={event => onBiographyChange(artist.id, event.target.value)}
    />
    <ArtistPortraitPicker
      artistName={artist.name}
      handle={portraitHandle}
      onChange={nextHandle => onPortraitChange(artist.id, nextHandle)}
      disabled={disabled}
    />
    <label className="flex min-w-0 flex-col gap-1 text-xs">
      <span className="font-medium text-gray-700">Portrait caption</span>
      <input
        type="text"
        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
        value={portraitCaption}
        disabled={disabled}
        placeholder={defaultPortraitCaption(artist)}
        onChange={event =>
          onPortraitCaptionChange(artist.id, event.target.value)
        }
      />
    </label>
  </div>
);

/**
 * The gap between two pages, where another one can go. Opens the card grid in
 * place, so the page lands exactly where the user pointed.
 */
const AddPageHere = ({ open, onOpen, onCancel, onPick, disabled }) =>
  open ? (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
      <div className="flex items-center justify-between pb-2">
        <span className="text-sm font-medium">Add a page here</span>
        <button
          type="button"
          className="text-xs font-medium text-gray-500 hover:text-gray-800"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      <BrochureTemplateCards onPick={onPick} disabled={disabled} />
    </div>
  ) : (
    <button
      type="button"
      disabled={disabled}
      onClick={onOpen}
      className="group flex items-center gap-2 py-0.5 text-xs text-gray-400 transition hover:text-blue-700 disabled:opacity-50"
    >
      <PlusIcon className="h-3.5 w-3.5" />
      <span className="opacity-0 transition group-hover:opacity-100">
        Add a page here
      </span>
      <span className="h-px flex-1 bg-gray-100 transition group-hover:bg-blue-200" />
    </button>
  );

/**
 * The words on a Free Text page. The title prints in capitals, and each
 * paragraph opens with an indent rather than a blank line, so the body reads as
 * continuous prose. A body longer than a page runs on, and an optional image
 * sits below it by default.
 */
const FreeTextFields = ({ content, precis, onChange, disabled }) => (
  <div className="flex min-w-0 flex-col gap-3">
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-gray-700">
          Title (prints in caps)
        </span>
        <input
          type="text"
          className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          value={content.title}
          disabled={disabled}
          onChange={event => onChange({ title: event.target.value })}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-gray-700">Subtitle</span>
        <input
          type="text"
          className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          value={content.subtitle}
          disabled={disabled}
          onChange={event => onChange({ subtitle: event.target.value })}
        />
      </label>
    </div>
    <div className="flex flex-col gap-1 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-medium text-gray-700">Body</span>
        <button
          type="button"
          disabled={disabled || !precis?.trim()}
          className="font-medium text-blue-600 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:text-gray-300"
          title={
            precis?.trim()
              ? "Insert this artwork's précis"
              : "This artwork has no précis"
          }
          onClick={() =>
            onChange({ body: appendPrecisToBody(content.body, precis) })
          }
        >
          Insert Precis
        </button>
      </div>
      <textarea
        aria-label="Body"
        className="h-40 rounded border border-gray-300 px-2 py-1.5 text-sm"
        value={content.body}
        disabled={disabled}
        placeholder="One paragraph per line, or a blank line between paragraphs if you are pasting. Each paragraph opens with an 8mm indent, and a long body runs on to more pages."
        onChange={event => onChange({ body: event.target.value })}
      />
    </div>
    <div>
      <div className="text-xs font-medium text-gray-700">Image sits</div>
      <div className="mt-1 flex flex-wrap gap-2">
        {FREE_TEXT_IMAGE_POSITIONS.map(option => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            aria-pressed={content.imagePosition === option.id}
            onClick={() => onChange({ imagePosition: option.id })}
            className={`rounded border px-2 py-1 text-xs transition disabled:opacity-50 ${
              content.imagePosition === option.id
                ? "border-blue-600 bg-blue-50 font-medium text-blue-700"
                : "border-gray-200 text-gray-600 hover:border-gray-400"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-1 text-xs text-gray-500">
        Images are attached to the bottom of the page by default and can be
        removed from the image picker.
      </div>
    </div>
  </div>
);

/**
 * One page of a work's section: what it is, the images it uses and the controls
 * to move or drop it.
 */
const SortablePageRow = ({
  page,
  position,
  images,
  precis,
  artists,
  orientations,
  onImageLoad,
  onSelectImage,
  onContentChange,
  onOrientationChange,
  onRemove,
  publicCollectionsValue,
  onPublicCollectionsChange,
  biographiesValue,
  onBiographyChange,
  artistPortraitsValue,
  onArtistPortraitChange,
  portraitCaptionsValue,
  onPortraitCaptionChange,
  disabled,
}) => {
  const id = sectionPageId(page);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const slots = templateSlots(page.template);
  // The pages whose words (or portrait) are the user's to write, and so open a
  // panel below the row even though they have no image slots to pick.
  const isArtistPage =
    page.template === "publicCollections" || page.template === "biography";
  // Nothing set yet shows the shape of the image the page will print — the one
  // in its slot, or the primary image an unset slot falls back to — since that
  // is what the renderer would read for itself. Unknown until the thumbnail has
  // loaded, and then no shape reads as chosen.
  const orientation =
    page.imageOrientation ||
    orientations[page.imageSlots?.[slots[0]?.key] ?? images[0]?.archiveId] ||
    null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="min-w-0 rounded-lg border border-gray-200 bg-white p-3"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-3 lg:flex-nowrap">
        <button
          type="button"
          className="cursor-grab text-gray-400 hover:text-gray-700"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
        <div className="w-5 text-sm font-bold text-gray-400">{position}</div>
        <TemplateSketch
          template={page.template}
          imageOrientation={orientation}
          className="h-12 w-9"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            {templateLabel(page.template)}
          </div>
          {templateDescription(page.template) ? (
            <div className="text-xs text-gray-500">
              {templateDescription(page.template)}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          disabled={disabled || !onRemove}
          className="text-gray-400 enabled:hover:text-red-600 disabled:opacity-30"
          title={
            onRemove
              ? "Remove this page"
              : "A work needs at least one page — remove the work itself from the Arrange step instead"
          }
          onClick={onRemove}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {slots.length > 0 || isArtistPage ? (
        <div className="mt-3 flex min-w-0 flex-col gap-4 lg:pl-11">
          {isFreeTextTemplate(page.template) ? (
            <FreeTextFields
              content={page.content}
              precis={precis}
              onChange={onContentChange}
              disabled={disabled}
            />
          ) : null}
          {slots.map(slot => (
            <Fragment key={slot.key}>
              <SlotCarousel
                slot={slot}
                images={images}
                selectedArchiveId={page.imageSlots?.[slot.key]}
                orientations={orientations}
                disabled={disabled}
                onSelect={archiveId => onSelectImage(slot.key, archiveId)}
                onImageLoad={onImageLoad}
              />
              {isFreeTextTemplate(page.template) &&
              page.imageSlots?.[slot.key] != null ? (
                <button
                  type="button"
                  disabled={disabled}
                  className="-mt-3 self-start text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                  onClick={() =>
                    onSelectImage(slot.key, page.imageSlots[slot.key])
                  }
                >
                  Remove image
                </button>
              ) : null}
            </Fragment>
          ))}
          {usesImageOrientation(page.template) ? (
            <ImageOrientationPicker
              value={orientation}
              onChange={onOrientationChange}
              disabled={disabled}
            />
          ) : null}
          {page.template === "publicCollections" && onPublicCollectionsChange
            ? artists.map(artist => (
                <label key={artist.id} className="flex flex-col gap-1 text-xs">
                  <span className="font-medium text-gray-700">
                    {artist.name}
                  </span>
                  <textarea
                    className="h-32 rounded border border-gray-300 px-2 py-1.5 text-sm"
                    value={
                      publicCollectionsValue[artist.id] ??
                      artist.publicCollections ??
                      ""
                    }
                    disabled={disabled}
                    placeholder="One collection per line…"
                    onChange={event =>
                      onPublicCollectionsChange(artist.id, event.target.value)
                    }
                  />
                </label>
              ))
            : null}
          {page.template === "biography" && onBiographyChange
            ? artists.map(artist => (
                <BiographyFields
                  key={artist.id}
                  artist={artist}
                  biography={
                    biographiesValue[artist.id] ?? artistBiography(artist)
                  }
                  onBiographyChange={onBiographyChange}
                  portraitHandle={artistPortraitsValue[artist.id] ?? null}
                  onPortraitChange={onArtistPortraitChange}
                  portraitCaption={
                    portraitCaptionsValue[artist.id] ??
                    defaultPortraitCaption(artist)
                  }
                  onPortraitCaptionChange={onPortraitCaptionChange}
                  disabled={disabled}
                />
              ))
            : null}
          {isArtistPage && artists.length === 0 ? (
            <div className="text-xs text-gray-500">
              No artists found for this instance.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * One work's section as an ordered list of pages: drag to reorder, drop the ones
 * that aren't wanted and add as many as the work deserves — a second Two Details
 * page with different images, say, or a Full Bleed between the Artwork Hero and
 * the biography. Unset image slots fall back (server-side) to the artwork's
 * primary image.
 *
 * `instanceId` is the work whose pages these are. The editor stays mounted as
 * the wizard steps from one work to the next, so it is also what tells the list
 * it is now looking at something else and should start from the top again.
 */
const BrochurePageList = ({
  instanceId = null,
  pages = [],
  onChange,
  images = [],
  precis = "",
  artists = [],
  publicCollectionsValue = {},
  onPublicCollectionsChange,
  biographiesValue = {},
  onBiographyChange,
  artistPortraitsValue = {},
  onArtistPortraitChange,
  portraitCaptionsValue = {},
  onPortraitCaptionChange,
  disabled = false,
}) => {
  // Where the card grid is open, as an index into `pages`.
  const [addingAt, setAddingAt] = useState(null);
  // Detected per-image orientation, keyed by archiveId — filled in as each
  // thumbnail loads, so a thumbnail shows in its natural aspect and picking one
  // for the Artwork Hero can set the shape that page anchors it by.
  const [orientations, setOrientations] = useState({});
  const pageListRef = useRef(null);

  // Another work's pages open at their first page, with nothing half-done left
  // over: how far down the last work you had read says nothing about where to
  // start reading this one, and the card grid you left open belonged to a row
  // that isn't here any more.
  useEffect(() => {
    setAddingAt(null);
    const pageList = pageListRef.current;
    if (pageList) pageList.scrollTop = 0;
  }, [instanceId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleImageLoad = archiveId => event => {
    const orientation = detectImageOrientation(event.target);
    setOrientations(previous =>
      previous[archiveId] === orientation
        ? previous
        : { ...previous, [archiveId]: orientation },
    );
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = pages.map(sectionPageId);
    onChange(reorderList(pages, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  const addPage = (index, template) => {
    onChange(
      insertSectionPage(
        pages,
        index,
        sectionPage({ key: nextPageKey(pages), template }),
      ),
    );
    setAddingAt(null);
  };

  const addPageHereProps = index => ({
    open: addingAt === index,
    onOpen: () => setAddingAt(index),
    onCancel: () => setAddingAt(null),
    onPick: template => addPage(index, template),
    disabled,
  });

  return (
    <div className="min-w-0 pt-4">
      <div className="text-sm font-medium">Brochure Content</div>
      <div className="mt-1 text-xs text-gray-500">
        The pages this work prints, in order. Drag to rearrange them, add as
        many as you like and assign an image to each slot — unset slots use the
        primary image.
      </div>
      {images.length === 0 ? (
        <div className="mt-2 text-xs text-gray-500">
          This instance&apos;s artwork has no images, so its pages will print
          without any.
        </div>
      ) : null}

      <div
        ref={pageListRef}
        className="mt-3 flex max-h-[50dvh] min-w-0 flex-col gap-1 overflow-y-auto pr-1 lg:max-h-[50vh]"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pages.map(sectionPageId)}
            strategy={verticalListSortingStrategy}
          >
            {pages.map((page, index) => (
              <Fragment key={sectionPageId(page)}>
                <AddPageHere {...addPageHereProps(index)} />
                <SortablePageRow
                  page={page}
                  position={index + 1}
                  images={images}
                  precis={precis}
                  artists={artists}
                  orientations={orientations}
                  onImageLoad={handleImageLoad}
                  onSelectImage={(slotKey, archiveId) =>
                    onChange(
                      updateSectionPage(
                        pages,
                        sectionPageId(page),
                        patchForPickedImage({
                          page,
                          slotKey,
                          archiveId,
                          orientation: orientations[archiveId],
                        }),
                      ),
                    )
                  }
                  onContentChange={patch =>
                    onChange(
                      updateSectionPage(pages, sectionPageId(page), {
                        content: { ...page.content, ...patch },
                      }),
                    )
                  }
                  onOrientationChange={imageOrientation =>
                    onChange(
                      updateSectionPage(pages, sectionPageId(page), {
                        imageOrientation,
                      }),
                    )
                  }
                  onRemove={
                    // A work prints at least one page: an empty section is
                    // indistinguishable from an unarranged one, and a work with
                    // nothing to show belongs out of the document instead.
                    pages.length > 1
                      ? () =>
                          onChange(
                            removeSectionPage(pages, sectionPageId(page)),
                          )
                      : null
                  }
                  publicCollectionsValue={publicCollectionsValue}
                  onPublicCollectionsChange={onPublicCollectionsChange}
                  biographiesValue={biographiesValue}
                  onBiographyChange={onBiographyChange}
                  artistPortraitsValue={artistPortraitsValue}
                  onArtistPortraitChange={onArtistPortraitChange}
                  portraitCaptionsValue={portraitCaptionsValue}
                  onPortraitCaptionChange={onPortraitCaptionChange}
                  disabled={disabled}
                />
              </Fragment>
            ))}
          </SortableContext>
        </DndContext>
        <AddPageHere {...addPageHereProps(pages.length)} />
      </div>
    </div>
  );
};

export { BrochurePageList };
export default BrochurePageList;
